import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, CircleDollarSign, Eraser, FileText, Router, Search, ShieldCheck, XCircle } from 'lucide-react';
import service from 'features/comercial/services/comissaoReguaService';

/**
 * Consulta cruzada de CPF ou CNPJ antes de fechar a venda.
 *
 * MOV consulta XT e XT consulta MOV. Quem lê o IXC de destino é um fluxo do n8n; a resposta é
 * sempre uma de três, e a tela mostra a
 * resposta antes de qualquer detalhe — é a pergunta que a vendedora fez.
 *
 * Erro de consulta aparece como erro, nunca como "pode contratar".
 */

const RETORNOS = {
  pode_contratar: {
    titulo: 'Pode contratar',
    Icone: CheckCircle2,
    cores: 'border-[color:var(--status-good-border)] bg-[color:var(--status-good-chip)] text-[color:var(--status-good-strong)]',
  },
  nao_pode_contratar: {
    titulo: 'Não pode contratar',
    Icone: XCircle,
    cores: 'border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-chip)] text-[color:var(--status-danger-strong)]',
  },
  avisar_supervisor: {
    titulo: 'Avise o supervisor antes de vender',
    Icone: AlertTriangle,
    cores: 'border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-chip)] text-[color:var(--status-warning-strong)]',
  },
};

const reais = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dia = (valor) => (valor ? String(valor).slice(0, 10).split('-').reverse().join('/') : '—');
const plural = (quantidade, um, varios) => `${quantidade} ${quantidade === 1 ? um : varios}`;
const formatarDocumento = (valor) => {
  const digitos = String(valor).replace(/\D/g, '').slice(0, 14);
  if (digitos.length <= 11) return digitos
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  return digitos
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const caixa = 'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)]';

const Fato = ({ rotulo, valor, destaque = false }) => (
  <div className="grid gap-1 border-t border-[color:var(--border)] py-3 text-sm first:border-t-0 sm:grid-cols-[minmax(0,1fr)_minmax(9rem,auto)] sm:items-baseline sm:gap-6">
    <dt className="text-[color:var(--text-muted)]">{rotulo}</dt>
    <dd className={`tabular-nums sm:text-right ${destaque ? 'font-semibold text-[color:var(--status-danger-strong)]' : 'font-medium text-[color:var(--text-primary)]'}`}>{valor}</dd>
  </div>
);

const GrupoDeFatos = ({ titulo, Icone, children }) => (
  <section className="min-w-0">
    <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold text-[color:var(--text-primary)]">
      <Icone className="h-4 w-4 text-[color:var(--accent)]" aria-hidden="true" />
      {titulo}
    </h4>
    <dl>{children}</dl>
  </section>
);

const Resultado = ({ dados, empresaConsultada }) => {
  const { titulo, Icone, cores } = RETORNOS[dados.retorno];
  const { contrato, financeiro } = dados;
  // O primeiro alerta é a mensagem principal; os outros vão para a lista.
  const outros = (dados.alertas || []).slice(1);

  return (
    <div className="space-y-4">
      <section className={`rounded-2xl border p-5 sm:p-6 ${cores}`} role="status" aria-live="polite">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[color:var(--surface)]">
            <Icone className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-xl font-bold tracking-tight sm:text-2xl">{titulo}</h3>
            <p className="mt-1.5 max-w-2xl text-sm leading-6">{dados.mensagem}</p>
            <p className="mt-3 text-xs opacity-80">Documento {dados.documento} · Consulta realizada em {dia(dados.consultado_em)}</p>
          </div>
        </div>
      </section>

      {outros.length > 0 && (
        <section className={`${caixa} overflow-hidden`}>
          <h3 className="flex items-center gap-2 px-5 pb-3 pt-5 font-semibold text-[color:var(--text-primary)]">
            <AlertTriangle className="h-4 w-4 text-[color:var(--status-warning-strong)]" aria-hidden="true" />
            Outros pontos para atenção
          </h3>
          <ul className="border-t border-[color:var(--border)]">
            {outros.map((alerta) => (
              <li key={alerta.codigo} className="border-t border-[color:var(--border)] px-5 py-3 text-sm leading-6 text-[color:var(--text-primary)] first:border-t-0">{alerta.mensagem}</li>
            ))}
          </ul>
        </section>
      )}

      {dados.tem_vinculo && contrato && financeiro && (
        <section className={`${caixa} overflow-hidden`}>
          <div className="flex items-center gap-3 border-b border-[color:var(--border)] px-5 py-4 sm:px-6">
            <FileText className="h-5 w-5 text-[color:var(--accent)]" aria-hidden="true" />
            <div>
              <h3 className="font-semibold text-[color:var(--text-primary)]">Dados encontrados na {empresaConsultada}</h3>
              <p className="mt-0.5 text-xs text-[color:var(--text-muted)]">Informações que sustentam o resultado acima.</p>
            </div>
          </div>
          <div className="grid gap-x-8 gap-y-6 p-5 sm:p-6 lg:grid-cols-2">
            <GrupoDeFatos titulo="Vínculo" Icone={ShieldCheck}>
              <Fato rotulo="Contratos ativos" valor={contrato.ativos} />
              <Fato rotulo="Contratos encerrados" valor={contrato.cancelados + contrato.negativados} />
              <Fato rotulo="Cliente desde" valor={dia(contrato.primeira_ativacao)} />
              <Fato rotulo="Último encerramento" valor={dia(contrato.ultima_saida)} />
            </GrupoDeFatos>
            <GrupoDeFatos titulo="Financeiro" Icone={CircleDollarSign}>
              <Fato
                rotulo={`Antes de ${dia(dados.data_de_corte)}`}
                valor={financeiro.pendencias_antigas.quantidade
                  ? `${plural(financeiro.pendencias_antigas.quantidade, 'título', 'títulos')} · ${reais(financeiro.pendencias_antigas.valor)}`
                  : 'Nenhuma pendência'}
              />
              <Fato
                rotulo={`Desde ${dia(dados.data_de_corte)}`}
                valor={financeiro.pendencias_recentes.quantidade
                  ? `${plural(financeiro.pendencias_recentes.quantidade, 'título', 'títulos')} · ${reais(financeiro.pendencias_recentes.valor)}`
                  : 'Nenhuma pendência'}
              />
              <Fato rotulo="Pagamentos registrados" valor={financeiro.pagamentos} />
              <Fato rotulo="Último pagamento" valor={dia(financeiro.ultimo_pagamento)} />
            </GrupoDeFatos>
            <div className="lg:col-span-2">
              <GrupoDeFatos titulo="Equipamentos" Icone={Router}>
                <Fato
                  rotulo="Não devolvidos"
                  valor={dados.equipamentos_nao_devolvidos || 'Nenhum'}
                  destaque={dados.equipamentos_nao_devolvidos > 0}
                />
              </GrupoDeFatos>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const ConsultaXt = ({ empresaConsultada = 'XT' }) => {
  const [documento, setDocumento] = useState('');
  const [dados, setDados] = useState(null);
  const [consultando, setConsultando] = useState(false);
  const [erroDoCampo, setErroDoCampo] = useState('');
  const [erro, setErro] = useState('');

  const consultar = async (evento) => {
    evento?.preventDefault();
    const digitos = documento.replace(/\D/g, '');
    if (digitos.length !== 11 && digitos.length !== 14) {
      setErroDoCampo('Informe um CPF (11 dígitos) ou um CNPJ (14 dígitos).');
      return;
    }

    setConsultando(true);
    setErro('');
    setErroDoCampo('');
    setDados(null);
    try {
      const resposta = await service.consultarInterempresa(digitos);
      // Só o que está no contrato vira resultado. O backend já barra o resto; a tela não confia.
      if (!RETORNOS[resposta?.retorno]) throw new Error('resposta fora do contrato');
      setDados(resposta);
    } catch (falha) {
      if (falha.response?.status === 422) {
        setErroDoCampo(falha.response.data?.errors?.documento?.[0] || falha.response.data?.message || 'CPF ou CNPJ inválido.');
      } else if (falha.response?.status === 429) {
        setErro('Muitas consultas em pouco tempo. Aguarde um minuto e tente de novo.');
      } else {
        setErro(`Não foi possível consultar a ${empresaConsultada} agora. Isso não quer dizer que o cliente pode contratar.`);
      }
    } finally {
      setConsultando(false);
    }
  };

  // Mudar o documento apaga a resposta: ela era de outra pessoa.
  const editar = (valor) => {
    setDocumento(formatarDocumento(valor));
    setDados(null);
    setErro('');
    setErroDoCampo('');
  };

  return (
    <div className="max-w-4xl space-y-5">
      <form onSubmit={consultar} className={`${caixa} overflow-hidden`} noValidate aria-busy={consultando}>
        <div className="border-b border-[color:var(--border)] px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[color:var(--status-info-bg)] text-[color:var(--status-info-fg)]">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-[color:var(--text-primary)]">Consulte antes de fechar a venda</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-[color:var(--text-muted)]">Verifique na {empresaConsultada} se há vínculo, pendências financeiras ou equipamentos não devolvidos.</p>
            </div>
          </div>
        </div>
        <div className="p-5 sm:p-6">
          <label htmlFor="consulta-xt-documento" className="block text-sm font-semibold text-[color:var(--text-primary)]">CPF ou CNPJ do cliente</label>
          <p id="consulta-xt-ajuda" className="mt-1 text-sm text-[color:var(--text-muted)]">Digite ou cole o documento, com ou sem pontuação.</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" aria-hidden="true" />
              <input
                id="consulta-xt-documento"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={documento}
                onChange={(evento) => editar(evento.target.value)}
                placeholder="CPF ou CNPJ"
                aria-invalid={Boolean(erroDoCampo)}
                aria-describedby={erroDoCampo ? 'consulta-xt-ajuda consulta-xt-erro' : 'consulta-xt-ajuda'}
                className={`min-h-11 w-full rounded-lg border bg-[color:var(--surface-muted)] py-2 pl-10 pr-11 text-base tabular-nums text-[color:var(--text-primary)] transition focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] ${erroDoCampo ? 'border-[color:var(--status-danger-strong)]' : 'border-[color:var(--border)]'}`}
              />
              {documento && !consultando && (
                <button type="button" onClick={() => editar('')} aria-label="Limpar documento" className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-[color:var(--text-muted)] transition hover:bg-[color:var(--header-hover-soft)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]">
                  <Eraser className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={consultando}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[color:var(--accent)] px-5 text-sm font-semibold text-white transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              {consultando ? 'Consultando' : `Consultar na ${empresaConsultada}`}
            </button>
          </div>
          {erroDoCampo && <p id="consulta-xt-erro" role="alert" className="mt-2 text-sm text-[color:var(--status-danger-strong)]">{erroDoCampo}</p>}
        </div>
      </form>

      {erro && (
        <p role="alert" className="flex items-start gap-2 rounded-lg border border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-chip)] p-3 text-sm text-[color:var(--status-danger-strong)]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{erro}</span>
          <button type="button" onClick={() => consultar()} className="shrink-0 rounded font-semibold underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-current">Tentar de novo</button>
        </p>
      )}

      {dados && <Resultado dados={dados} empresaConsultada={empresaConsultada} />}
    </div>
  );
};

export default ConsultaXt;
